import { useState, useEffect } from 'react'
import { FloppyDiskIcon, ArrowCounterClockwiseIcon } from '@phosphor-icons/react'
import {
    ScoopButton,
    ScoopTextField,
    ScoopSlider,
    ScoopText,
    ScoopSpinner,
    ScoopRow,
    ScoopColumn,
    ScoopCenter,
} from '../../../../../ui'
import { FormField, FormSection } from '../shared'
import styles from './OrchestrationForm.module.scss'

const EMPTY_VALUES = {
    max_rounds: 1,
    max_depth: 1,
    max_parallel: 1,
    max_spawns_per_investigation: 1,
    termination: {
        success_threshold: 0,
        max_time_seconds: 0,
        low_priority_threshold: 0,
    },
    priority: {
        default_priority: 0,
        priority_step: 0,
        depth_penalty: 0,
    },
    cost_controls: {
        max_ai_calls_per_query: 0,
        token_budget_per_call: 0,
    },
}

const DEFAULT_VALUES = {
    max_rounds: 5,
    max_depth: 4,
    max_parallel: 3,
    max_spawns_per_investigation: 3,
    termination: {
        success_threshold: 0.95,
        max_time_seconds: 300,
        low_priority_threshold: 0.3,
    },
    priority: {
        default_priority: 0.8,
        priority_step: 0.2,
        depth_penalty: 0.1,
    },
    cost_controls: {
        max_ai_calls_per_query: 50,
        token_budget_per_call: 100000,
    },
}

/**
 * OrchestrationForm - Form for editing orchestration.json configuration
 *
 * @param {Object} data - Current orchestration content
 * @param {Function} onSave - Callback when saving (receives updated data)
 * @param {boolean} isSaving - Whether save is in progress
 * @param {boolean} isLoading - Whether data is loading
 */
export const OrchestrationForm = ({ data, onSave, isSaving, isLoading, onDirtyStateChange }) => {
    const [formData, setFormData] = useState(data ? DEFAULT_VALUES : EMPTY_VALUES)
    const [hasChanges, setHasChanges] = useState(false)

    // Initialize form data when data prop changes
    useEffect(() => {
        if (data) {
            setFormData({
                max_rounds: data.max_rounds ?? DEFAULT_VALUES.max_rounds,
                max_depth: data.max_depth ?? DEFAULT_VALUES.max_depth,
                max_parallel: data.max_parallel ?? DEFAULT_VALUES.max_parallel,
                max_spawns_per_investigation:
                    data.max_spawns_per_investigation ??
                    DEFAULT_VALUES.max_spawns_per_investigation,
                termination: {
                    success_threshold:
                        data.termination?.success_threshold ??
                        DEFAULT_VALUES.termination.success_threshold,
                    max_time_seconds:
                        data.termination?.max_time_seconds ??
                        DEFAULT_VALUES.termination.max_time_seconds,
                    low_priority_threshold:
                        data.termination?.low_priority_threshold ??
                        DEFAULT_VALUES.termination.low_priority_threshold,
                },
                priority: {
                    default_priority:
                        data.priority?.default_priority ?? DEFAULT_VALUES.priority.default_priority,
                    priority_step:
                        data.priority?.priority_step ?? DEFAULT_VALUES.priority.priority_step,
                    depth_penalty:
                        data.priority?.depth_penalty ?? DEFAULT_VALUES.priority.depth_penalty,
                },
                cost_controls: {
                    max_ai_calls_per_query:
                        data.cost_controls?.max_ai_calls_per_query ??
                        DEFAULT_VALUES.cost_controls.max_ai_calls_per_query,
                    token_budget_per_call:
                        data.cost_controls?.token_budget_per_call ??
                        DEFAULT_VALUES.cost_controls.token_budget_per_call,
                },
            })
            setHasChanges(false)
        }
    }, [data])

    useEffect(() => {
        onDirtyStateChange?.(hasChanges)
    }, [hasChanges, onDirtyStateChange])

    const handleChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }))
        setHasChanges(true)
    }

    const handleTerminationChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            termination: {
                ...prev.termination,
                [field]: value,
            },
        }))
        setHasChanges(true)
    }

    const handleCostChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            cost_controls: {
                ...prev.cost_controls,
                [field]: value,
            },
        }))
        setHasChanges(true)
    }

    const handlePriorityChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            priority: {
                ...prev.priority,
                [field]: value,
            },
        }))
        setHasChanges(true)
    }

    const handleSave = () => {
        // Merge form data with existing data to preserve other fields
        const updatedData = {
            ...data,
            ...formData,
        }
        onSave(updatedData)
        setHasChanges(false)
    }

    const handleReset = () => {
        if (data) {
            setFormData({
                max_rounds: data.max_rounds ?? DEFAULT_VALUES.max_rounds,
                max_depth: data.max_depth ?? DEFAULT_VALUES.max_depth,
                max_parallel: data.max_parallel ?? DEFAULT_VALUES.max_parallel,
                max_spawns_per_investigation:
                    data.max_spawns_per_investigation ??
                    DEFAULT_VALUES.max_spawns_per_investigation,
                termination: {
                    success_threshold:
                        data.termination?.success_threshold ??
                        DEFAULT_VALUES.termination.success_threshold,
                    max_time_seconds:
                        data.termination?.max_time_seconds ??
                        DEFAULT_VALUES.termination.max_time_seconds,
                    low_priority_threshold:
                        data.termination?.low_priority_threshold ??
                        DEFAULT_VALUES.termination.low_priority_threshold,
                },
                priority: {
                    default_priority:
                        data.priority?.default_priority ?? DEFAULT_VALUES.priority.default_priority,
                    priority_step:
                        data.priority?.priority_step ?? DEFAULT_VALUES.priority.priority_step,
                    depth_penalty:
                        data.priority?.depth_penalty ?? DEFAULT_VALUES.priority.depth_penalty,
                },
                cost_controls: {
                    max_ai_calls_per_query:
                        data.cost_controls?.max_ai_calls_per_query ??
                        DEFAULT_VALUES.cost_controls.max_ai_calls_per_query,
                    token_budget_per_call:
                        data.cost_controls?.token_budget_per_call ??
                        DEFAULT_VALUES.cost_controls.token_budget_per_call,
                },
            })
            setHasChanges(false)
        }
    }

    if (isLoading) {
        return (
            <ScoopCenter className={styles.loading}>
                <ScoopSpinner size={32} />
                <ScoopText>Loading settings...</ScoopText>
            </ScoopCenter>
        )
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.titleSection}>
                    <ScoopText as="h2" className={styles.title}>
                        Investigation Settings
                    </ScoopText>
                    <ScoopText className={styles.description}>
                        Configure how investigations are executed, including limits, termination
                        conditions, and cost controls.
                    </ScoopText>
                </div>
                <ScoopRow className={styles.actions}>
                    {hasChanges && (
                        <ScoopButton
                            variant="grey"
                            size="sm"
                            onClick={handleReset}
                            disabled={isSaving}
                            leftSection={<ArrowCounterClockwiseIcon size={16} />}
                        >
                            Reset
                        </ScoopButton>
                    )}
                    <ScoopButton
                        variant="default"
                        size="sm"
                        onClick={handleSave}
                        disabled={!hasChanges || isSaving}
                        loading={isSaving}
                        leftSection={<FloppyDiskIcon size={16} />}
                    >
                        Save Changes
                    </ScoopButton>
                </ScoopRow>
            </div>

            <div className={styles.content}>
                {/* Investigation Limits Section */}
                <FormSection
                    title="Investigation Limits"
                    description="Control how deep and wide investigations can go. Higher values allow more thorough analysis but increase processing time."
                    defaultExpanded={!!data}
                >
                    <div className={styles.sliderGrid}>
                        <FormField
                            label="Maximum Rounds"
                            helpText="Maximum number of investigation iterations before stopping. Recommended: 5"
                        >
                            <div className={styles.sliderRow}>
                                <ScoopSlider
                                    value={formData.max_rounds}
                                    onChange={(value) => handleChange('max_rounds', value)}
                                    min={1}
                                    max={10}
                                    step={1}
                                    disabled={isSaving}
                                    showMinMaxLabels
                                    valueLabelDisplay
                                />
                                <ScoopText className={styles.sliderValue}>
                                    {formData.max_rounds}
                                </ScoopText>
                            </div>
                        </FormField>

                        <FormField
                            label="Maximum Depth"
                            helpText="How deep investigations can drill down into data. Recommended: 4"
                        >
                            <div className={styles.sliderRow}>
                                <ScoopSlider
                                    value={formData.max_depth}
                                    onChange={(value) => handleChange('max_depth', value)}
                                    min={1}
                                    max={6}
                                    step={1}
                                    disabled={isSaving}
                                    showMinMaxLabels
                                    valueLabelDisplay
                                />
                                <ScoopText className={styles.sliderValue}>
                                    {formData.max_depth}
                                </ScoopText>
                            </div>
                        </FormField>

                        <FormField
                            label="Parallel Queries"
                            helpText="Number of concurrent queries that can run simultaneously. Recommended: 3"
                        >
                            <div className={styles.sliderRow}>
                                <ScoopSlider
                                    value={formData.max_parallel}
                                    onChange={(value) => handleChange('max_parallel', value)}
                                    min={1}
                                    max={10}
                                    step={1}
                                    disabled={isSaving}
                                    showMinMaxLabels
                                    valueLabelDisplay
                                />
                                <ScoopText className={styles.sliderValue}>
                                    {formData.max_parallel}
                                </ScoopText>
                            </div>
                        </FormField>

                        <FormField
                            label="Max Spawns per Investigation"
                            helpText="Maximum number of child investigations that can be spawned. Recommended: 3"
                        >
                            <div className={styles.sliderRow}>
                                <ScoopSlider
                                    value={formData.max_spawns_per_investigation}
                                    onChange={(value) =>
                                        handleChange('max_spawns_per_investigation', value)
                                    }
                                    min={1}
                                    max={20}
                                    step={1}
                                    disabled={isSaving}
                                    showMinMaxLabels
                                    valueLabelDisplay
                                />
                                <ScoopText className={styles.sliderValue}>
                                    {formData.max_spawns_per_investigation}
                                </ScoopText>
                            </div>
                        </FormField>
                    </div>
                </FormSection>

                {/* Termination Conditions Section */}
                <FormSection
                    title="Termination Conditions"
                    description="Define when investigations should stop. These settings balance thoroughness against resource usage."
                    defaultExpanded={!!data}
                >
                    <ScoopColumn className={styles.fields}>
                        <FormField
                            label="Success Threshold"
                            helpText="Confidence level (0-1) at which to stop investigating. Recommended: 95%"
                        >
                            <div className={styles.sliderRow}>
                                <ScoopSlider
                                    value={formData.termination.success_threshold}
                                    onChange={(value) =>
                                        handleTerminationChange('success_threshold', value)
                                    }
                                    min={0}
                                    max={1}
                                    step={0.05}
                                    disabled={isSaving}
                                    showMinMaxLabels
                                    valueLabelDisplay
                                />
                                <ScoopText className={styles.sliderValue}>
                                    {(formData.termination.success_threshold * 100).toFixed(0)}%
                                </ScoopText>
                            </div>
                        </FormField>

                        <FormField
                            label="Timeout (seconds)"
                            helpText="Maximum time in seconds before an investigation times out. Recommended: 300"
                        >
                            <ScoopTextField
                                type="number"
                                value={formData.termination.max_time_seconds}
                                onChange={(e) =>
                                    handleTerminationChange(
                                        'max_time_seconds',
                                        e.target.value === '' ? '' : parseInt(e.target.value) || 0
                                    )
                                }
                                disabled={isSaving}
                                className={styles.numberInput}
                            />
                        </FormField>

                        <FormField
                            label="Low Priority Threshold"
                            helpText="Score below which to abandon low-priority investigations. Recommended: 30%"
                        >
                            <div className={styles.sliderRow}>
                                <ScoopSlider
                                    value={formData.termination.low_priority_threshold}
                                    onChange={(value) =>
                                        handleTerminationChange('low_priority_threshold', value)
                                    }
                                    min={0}
                                    max={1}
                                    step={0.05}
                                    disabled={isSaving}
                                    showMinMaxLabels
                                    valueLabelDisplay
                                />
                                <ScoopText className={styles.sliderValue}>
                                    {(formData.termination.low_priority_threshold * 100).toFixed(0)}
                                    %
                                </ScoopText>
                            </div>
                        </FormField>
                    </ScoopColumn>
                </FormSection>

                {/* Priority Settings Section */}
                <FormSection
                    title="Priority Settings"
                    description="Control how investigation priorities are calculated. Higher priority investigations are processed first."
                    defaultExpanded={!!data}
                >
                    <ScoopColumn className={styles.fields}>
                        <FormField
                            label="Default Priority"
                            helpText="Starting priority for new investigations (0-1). Recommended: 80%"
                        >
                            <div className={styles.sliderRow}>
                                <ScoopSlider
                                    value={formData.priority.default_priority}
                                    onChange={(value) =>
                                        handlePriorityChange('default_priority', value)
                                    }
                                    min={0}
                                    max={1}
                                    step={0.05}
                                    disabled={isSaving}
                                    showMinMaxLabels
                                    valueLabelDisplay
                                />
                                <ScoopText className={styles.sliderValue}>
                                    {(formData.priority.default_priority * 100).toFixed(0)}%
                                </ScoopText>
                            </div>
                        </FormField>

                        <FormField
                            label="Priority Step"
                            helpText="How much priority changes between investigation levels. Recommended: 20%"
                        >
                            <div className={styles.sliderRow}>
                                <ScoopSlider
                                    value={formData.priority.priority_step}
                                    onChange={(value) =>
                                        handlePriorityChange('priority_step', value)
                                    }
                                    min={0}
                                    max={0.5}
                                    step={0.05}
                                    disabled={isSaving}
                                    showMinMaxLabels
                                    valueLabelDisplay
                                />
                                <ScoopText className={styles.sliderValue}>
                                    {(formData.priority.priority_step * 100).toFixed(0)}%
                                </ScoopText>
                            </div>
                        </FormField>

                        <FormField
                            label="Depth Penalty"
                            helpText="Priority reduction applied per depth level. Recommended: 10%"
                        >
                            <div className={styles.sliderRow}>
                                <ScoopSlider
                                    value={formData.priority.depth_penalty}
                                    onChange={(value) =>
                                        handlePriorityChange('depth_penalty', value)
                                    }
                                    min={0}
                                    max={0.5}
                                    step={0.05}
                                    disabled={isSaving}
                                    showMinMaxLabels
                                    valueLabelDisplay
                                />
                                <ScoopText className={styles.sliderValue}>
                                    {(formData.priority.depth_penalty * 100).toFixed(0)}%
                                </ScoopText>
                            </div>
                        </FormField>
                    </ScoopColumn>
                </FormSection>

                {/* Cost Controls Section */}
                <FormSection
                    title="Cost Controls"
                    description="Set limits on AI usage to control costs. These settings apply per investigation query."
                    defaultExpanded={!!data}
                >
                    <ScoopColumn className={styles.fields}>
                        <FormField
                            label="Max AI Calls per Query"
                            helpText="Maximum number of AI API calls allowed per query. Recommended: 50"
                        >
                            <ScoopTextField
                                type="number"
                                value={formData.cost_controls.max_ai_calls_per_query}
                                onChange={(e) =>
                                    handleCostChange(
                                        'max_ai_calls_per_query',
                                        e.target.value === '' ? '' : parseInt(e.target.value) || 0
                                    )
                                }
                                disabled={isSaving}
                                className={styles.numberInput}
                            />
                        </FormField>

                        <FormField
                            label="Token Budget per Call"
                            helpText="Maximum tokens allowed per AI call. Recommended: 100,000"
                        >
                            <ScoopTextField
                                type="number"
                                value={formData.cost_controls.token_budget_per_call}
                                onChange={(e) =>
                                    handleCostChange(
                                        'token_budget_per_call',
                                        e.target.value === '' ? '' : parseInt(e.target.value) || 0
                                    )
                                }
                                disabled={isSaving}
                                className={styles.numberInput}
                            />
                        </FormField>
                    </ScoopColumn>
                </FormSection>
            </div>
        </div>
    )
}
